export type InventoryCatalogRoomType =
  | 'General'
  | 'Salón'
  | 'Dormitorio'
  | 'Cocina'
  | 'Baño';

export type InventoryCatalogItem = {
  name: string;
  isRequired: boolean;
  defaultQuantity?: string;
};

export type InventoryCatalogEntry = {
  roomType: InventoryCatalogRoomType;
  items: InventoryCatalogItem[];
};

const required = (name: string, defaultQuantity = '1'): InventoryCatalogItem => ({
  name,
  isRequired: true,
  defaultQuantity,
});

const recommended = (name: string, defaultQuantity = '1'): InventoryCatalogItem => ({
  name,
  isRequired: false,
  defaultQuantity,
});

export const DEFAULT_INVENTORY_CATALOG: InventoryCatalogEntry[] = [
  {
    roomType: 'General',
    items: [
      required('Llaves'),
      required('Mandos'),
      required('Router'),
      required('Lavadora'),
      required('Tendedero (interior o exterior)'),
      required('Escoba y recogedor'),
      required('Fregona y cubo'),
      required('Luminarias / luces de todas las áreas'),
      recommended('Elementos decorativos'),
    ],
  },
  {
    roomType: 'Salón',
    items: [
      required('Mesa de comedor'),
      required('Sillas', '4'),
      required('Sofá o sillones'),
      recommended('Mesa de centro'),
      recommended('TV'),
    ],
  },
  {
    roomType: 'Dormitorio',
    items: [
      required('Cama: base/somier + colchón (mín. 90×190)'),
      required('Protector de colchón'),
      required('Armario o ropero'),
      required('Escritorio'),
      required('Silla de escritorio'),
      recommended('Mesita de noche'),
      required('Lámpara o flexo de escritorio'),
      required('Cortina/estor opaco o persiana (blackout)'),
      recommended('Ventilador o calefactor según ciudad'),
      recommended('Estantería o baldas'),
      recommended('Espejo'),
      recommended('Cerradura en la puerta de la habitación'),
    ],
  },
  {
    roomType: 'Cocina',
    items: [
      required('Frigorífico con congelador'),
      required('Placa de cocina (vitro/inducción/gas)'),
      required('Microondas'),
      recommended('Campana o extractor'),
      required('Cubo de basura'),
      required('Tragantes / desagües del fregadero'),
      required('Plato llano', '6'),
      required('Plato hondo', '6'),
      required('Vasos', '6'),
      required('Cubiertos completos (cuchara, tenedor, cuchillo, cucharilla)', '6'),
      required('Sartenes', '2'),
      required('Ollas / cazos', '2'),
      required('Cuchillo de cocina', '2'),
      required('Tabla de cortar'),
      required('Cucharón, espátula y cuchara de servir'),
      required('Escurreplatos'),
    ],
  },
  {
    roomType: 'Baño',
    items: [
      required('Espejo'),
      required('Cortina o mampara de ducha'),
      required('Escobilla de WC'),
      required('Papelera'),
      required('Grifería y ducha funcional con agua caliente'),
      required('Tragantes y juntas (lavabo/ducha)'),
      recommended('Mueble o estantería de baño'),
      recommended('Elementos decorativos'),
    ],
  },
];

export type InventorySectionPlan = {
  key: string;
  title: string;
  catalogRoomType: InventoryCatalogRoomType;
  order: number;
};

export const getCatalogItemsForRoomType = (
  roomType: InventoryCatalogRoomType | string | null | undefined,
  catalog: InventoryCatalogEntry[] = DEFAULT_INVENTORY_CATALOG,
): InventoryCatalogItem[] => {
  if (!roomType) return [];
  return catalog.find((entry) => entry.roomType === roomType)?.items ?? [];
};

export const getCatalogItemNamesForRoomType = (
  roomType: InventoryCatalogRoomType | string | null | undefined,
  catalog: InventoryCatalogEntry[] = DEFAULT_INVENTORY_CATALOG,
): string[] => {
  return getCatalogItemsForRoomType(roomType, catalog).map((item) => item.name);
};

export const parseCatalogQuantity = (value?: string | null): number | '' => {
  if (!value) return 1;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 1;
};

export const resolveCatalogRoomTypeFromSectionKey = (
  key: string,
): InventoryCatalogRoomType | null => {
  if (key === 'general') return 'General';
  if (key === 'salon') return 'Salón';
  if (key === 'cocina') return 'Cocina';
  if (key === 'bano' || key.startsWith('bano-')) return 'Baño';
  if (key.startsWith('dormitorio-')) return 'Dormitorio';
  return null;
};

export type RequiredInventoryItemCheck = {
  description?: string;
  isRequired?: boolean;
  isReviewed?: boolean;
  isPresentInProperty?: 'YES' | 'NO' | 'NOT_NEEDED' | boolean | null;
  providedBy?: string | null;
};

const QUALITY_STATUSES = ['EXCELLENT', 'GOOD', 'REGULAR', 'BAD'] as const;

const normalizePresence = (
  value?: 'YES' | 'NO' | 'NOT_NEEDED' | boolean | null,
): 'YES' | 'NO' | 'NOT_NEEDED' | null => {
  if (value === true || value === 'YES') return 'YES';
  if (value === false || value === 'NO') return 'NO';
  if (value === 'NOT_NEEDED') return 'NOT_NEEDED';
  return null;
};

export const isInventoryItemFilled = (item: {
  isPresentInProperty?: 'YES' | 'NO' | 'NOT_NEEDED' | boolean | null;
  providedBy?: string | null;
  status?: string | null;
}): boolean => {
  const presence = normalizePresence(item.isPresentInProperty);
  if (presence === 'NOT_NEEDED') return true;
  if (presence === 'NO') return Boolean(item.providedBy);
  if (presence === 'YES') {
    return QUALITY_STATUSES.some((status) => status === item.status);
  }
  return false;
};

export const isRequiredInventoryItemComplete = (
  item: RequiredInventoryItemCheck,
): boolean => {
  if (!item.isRequired) return true;
  return isInventoryItemFilled(item);
};

export const getIncompleteRequiredInventoryItems = (
  sections: Array<{ title?: string; items: RequiredInventoryItemCheck[] }>,
): RequiredInventoryItemCheck[] => {
  return sections.flatMap((section) =>
    section.items.filter(
      (item) => Boolean(item.description?.trim()) && !isRequiredInventoryItemComplete(item),
    ),
  );
};

export const formatIncompleteRequiredInventoryList = (
  sections: Array<{ title?: string; items: RequiredInventoryItemCheck[] }>,
): string[] => {
  return sections.flatMap((section) =>
    section.items
      .filter(
        (item) =>
          Boolean(item.description?.trim()) && !isRequiredInventoryItemComplete(item),
      )
      .map((item) => {
        const name = item.description!.trim();
        const room = section.title?.trim();
        return room ? `${room} · ${name}` : name;
      }),
  );
};

export const groupIncompleteRequiredInventoryItems = (
  sections: Array<{ title?: string; items: RequiredInventoryItemCheck[] }>,
): Array<{ room: string; items: string[] }> => {
  return sections
    .map((section) => {
      const items = section.items
        .filter(
          (item) =>
            Boolean(item.description?.trim()) && !isRequiredInventoryItemComplete(item),
        )
        .map((item) => item.description!.trim());
      if (items.length === 0) return null;
      return {
        room: section.title?.trim() || '—',
        items,
      };
    })
    .filter((group): group is { room: string; items: string[] } => group !== null);
};

export const getSectionPhotoCount = (section: {
  items: Array<{
    existingFiles?: unknown[] | null;
    newFiles?: unknown[] | null;
  }>;
}): number =>
  section.items.reduce(
    (sum, item) =>
      sum + (item.existingFiles?.length ?? 0) + (item.newFiles?.length ?? 0),
    0,
  );

export const sectionHasMinPhotos = (
  section: {
    items: Array<{
      existingFiles?: unknown[] | null;
      newFiles?: unknown[] | null;
    }>;
  },
  min = 1,
): boolean => getSectionPhotoCount(section) >= min;

export const getSectionsMissingPhotos = <
  T extends {
    title?: string;
    items: Array<{
      existingFiles?: unknown[] | null;
      newFiles?: unknown[] | null;
    }>;
  },
>(
  sections: T[],
  min = 1,
): T[] => sections.filter((section) => !sectionHasMinPhotos(section, min));

/**
 * Parses an inspection date as a local calendar day (avoids UTC midnight
 * shifting the day backward in negative-offset timezones).
 */
const toLocalCalendarDate = (value: string | Date): Date | null => {
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  if (Number.isNaN(value.getTime())) return null;
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
};

/** Editable for 2 calendar days from the inspection date (local time), inclusive. */
export const isInventoryEditableByDate = (
  inspectionDate?: string | Date | null,
): boolean => {
  if (!inspectionDate) return true;
  const start = toLocalCalendarDate(inspectionDate);
  if (!start) return true;
  const deadline = new Date(start);
  deadline.setDate(deadline.getDate() + 1);
  deadline.setHours(23, 59, 59, 999);
  return Date.now() <= deadline.getTime();
};

/** Normalizes GraphQL/DB type variants (INITIAL/FINAL vs initial/final). */
export const isFinalInventoryType = (type?: string | null): boolean => {
  const normalized = String(type ?? '')
    .trim()
    .toUpperCase();
  return normalized === 'FINAL';
};

type PropertyLikeForInventory = {
  commonBathroom?: boolean | null;
  unities?: Array<{ privateBathroom?: boolean | null }> | null;
  bedroomCount?: number | null;
  bathroomCount?: number | null;
};

export const buildDefaultSectionsForProperty = (
  property: PropertyLikeForInventory = {},
): InventorySectionPlan[] => {
  const unities = property.unities ?? [];
  // Prefer unities (same as backend). Fallbacks are only for the embed preview when
  // the parent did not pass room layout — backend still seeds from real unities on create.
  const bedroomCount =
    unities.length > 0
      ? unities.length
      : Math.max(0, property.bedroomCount ?? 1);
  const bathroomCountFromFlags =
    (property.commonBathroom ? 1 : 0) +
    unities.filter((unity) => unity.privateBathroom).length;
  const bathroomCount =
    bathroomCountFromFlags > 0
      ? bathroomCountFromFlags
      : Math.max(0, property.bathroomCount ?? (unities.length > 0 ? 0 : 1));

  const sections: InventorySectionPlan[] = [];
  let order = 0;

  sections.push({
    key: 'general',
    title: 'General',
    catalogRoomType: 'General',
    order: order++,
  });
  sections.push({
    key: 'salon',
    title: 'Salón',
    catalogRoomType: 'Salón',
    order: order++,
  });

  for (let i = 1; i <= bedroomCount; i++) {
    sections.push({
      key: `dormitorio-${i}`,
      title: `Dormitorio ${i}`,
      catalogRoomType: 'Dormitorio',
      order: order++,
    });
  }

  sections.push({
    key: 'cocina',
    title: 'Cocina',
    catalogRoomType: 'Cocina',
    order: order++,
  });

  if (bathroomCount === 1) {
    sections.push({
      key: 'bano',
      title: 'Baño',
      catalogRoomType: 'Baño',
      order: order++,
    });
  } else if (bathroomCount > 1) {
    for (let i = 1; i <= bathroomCount; i++) {
      sections.push({
        key: `bano-${i}`,
        title: `Baño ${i}`,
        catalogRoomType: 'Baño',
        order: order++,
      });
    }
  }

  return sections;
};
